import React, { useMemo, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

// Images
const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString("en-US");
const pct = (v) => {
  const n = Number(v);
  if (Number.isFinite(n)) return `${n.toFixed(1)}%`;
  // if API already sends like "3.2%" keep it
  if (typeof v === "string" && v.includes("%")) return v;
  return "0.0%";
};

const AeroTable4 = ({ apiData = [] }) => {
  const pdfRef = useRef(null);

  const computed = useMemo(() => {
    if (!apiData || apiData.length === 0) return null;

    const years = apiData.map((x) => String(x.year));
    const all = apiData.map((x) => num(x["All Sexes"]));
    const female = apiData.map((x) => num(x["Female"]));
    const male = apiData.map((x) => num(x["Male"]));

    const firstAll = all[0] || 0;
    const lastAll = all[all.length - 1] || 0;
    const overallGrowth =
      firstAll > 0
        ? (((lastAll - firstAll) / firstAll) * 100).toFixed(1)
        : "0.0";

    // Peak year (All Sexes)
    let peakIdx = 0;
    for (let i = 1; i < all.length; i++) {
      if (all[i] > all[peakIdx]) peakIdx = i;
    }

    // Best growth year (YoY) for total
    let bestYoYIdx = 1;
    let bestYoYGrowth = -Infinity;
    for (let i = 1; i < all.length; i++) {
      const prev = all[i - 1] || 0;
      const cur = all[i] || 0;
      const g = prev > 0 ? ((cur - prev) / prev) * 100 : 0;
      if (g > bestYoYGrowth) {
        bestYoYGrowth = g;
        bestYoYIdx = i;
      }
    }

    // 2024 shares (or last year if 2024 isn't present)
    const lastFemale = female[female.length - 1] || 0;
    const lastMale = male[male.length - 1] || 0;
    const lastTotal = lastAll || lastFemale + lastMale || 0;

    const femaleShare =
      lastTotal > 0 ? ((lastFemale / lastTotal) * 100).toFixed(1) : "0.0";
    const maleShare =
      lastTotal > 0 ? ((lastMale / lastTotal) * 100).toFixed(1) : "0.0";

    return {
      years,
      all,
      female,
      male,
      overallGrowth,
      peakYear: years[peakIdx],
      peakTotal: all[peakIdx],
      bestGrowthYear: years[bestYoYIdx],
      bestYoYGrowth,
      lastYear: years[years.length - 1],
      lastTotal,
      lastFemale,
      lastMale,
      femaleShare,
      maleShare,
    };
  }, [apiData]);

  const chartData = useMemo(() => {
    if (!computed) return { labels: [], datasets: [] };

    return {
      labels: computed.years,
      datasets: [
        {
          label: "All Sexes Employment",
          data: computed.all,
          backgroundColor: "#ffffbb",
          borderColor: "#ffffbb",
          borderWidth: 1,
        },
        {
          label: "Female Employment",
          data: computed.female,
          backgroundColor: "#0ea5e980",
          borderColor: "#0ea5e9",
          borderWidth: 1,
        },
        {
          label: "Male Employment",
          data: computed.male,
          backgroundColor: "#EC489980",
          borderColor: "#EC4899",
          borderWidth: 1,
        },
      ],
    };
  }, [computed]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Growth Rate by Gender (Employment)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" }, grid: { display: false } },
      y: {
        title: { display: true, text: "Employment Count" },
        beginAtZero: true,
        ticks: { callback: (v) => fmt(v) },
      },
    },
  };

    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text(APP_VERSION, 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );

        pdf.save("growth-rate-by-gender.pdf");
      });
    };

  if (!apiData || apiData.length === 0) {
    return <p>No data available</p>;
  }

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Growth Rate by Gender
        </h2>

        {/* Summary Cards */}
        {computed && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
              <div className="p-4 bg-blue-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Overall Growth ({computed.years[0]}–{computed.lastYear})
                </p>
                <p className="text-xl font-bold text-blue-800">
                  {Number(computed.overallGrowth) >= 0 ? "+" : ""}
                  {computed.overallGrowth}%
                </p>
              </div>

              <div className="p-4 bg-yellow-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Peak Year (All Sexes)
                </p>
                <p className="text-xl font-bold text-yellow-800">
                  {computed.peakYear}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Peak: {fmt(computed.peakTotal)}
                </p>
              </div>

              <div className="p-4 bg-green-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Best Total Growth Year (YoY)
                </p>
                <p className="text-xl font-bold text-green-800">
                  {computed.bestGrowthYear} (
                  {computed.bestYoYGrowth >= 0 ? "+" : ""}
                  {computed.bestYoYGrowth.toFixed(1)}%)
                </p>
              </div>
            </div>

            {/* Share Cards (last year) */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
              <div className="p-4 bg-pink-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Female ({computed.lastYear})
                </p>
                <p className="text-xl font-bold text-pink-800">
                  {fmt(computed.lastFemale)} ({computed.femaleShare}%)
                </p>
              </div>

              <div className="p-4 bg-blue-100 rounded-xl shadow">
                <p className="text-sm text-gray-600 font-medium">
                  Male ({computed.lastYear})
                </p>
                <p className="text-xl font-bold text-blue-800">
                  {fmt(computed.lastMale)} ({computed.maleShare}%)
                </p>
              </div>
            </div> */}
          </>
        )}

        {/* Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <Table
                striped
                bordered
                hover
                responsive
                className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden"
              >
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      All Sexes
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Female
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Male
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Total Growth %
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Female Growth %
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Male Growth %
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {apiData.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50/60"} hover:bg-gray-50`}
                    >
                      <td className="p-2 text-left font-medium">{row.year}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(row["All Sexes"])}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(row["Female"])}
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmt(row["Male"])}
                      </td>
                      <td className="p-2" style={{ textAlign: "right" }}>
                        {pct(row["Total Employment Growth %"])}
                      </td>
                      <td className="p-2" style={{ textAlign: "right" }}>
                        {pct(row["Women Growth %"])}
                      </td>
                      <td className="p-2" style={{ textAlign: "right" }}>
                        {pct(row["Men Growth %"])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* <p className="text-xs text-gray-500 mt-3">
              Tip: Scroll horizontally to view all columns on mobile.
            </p> */}
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[520px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="PDF Icon" />
        </button>
      </div>
    </>
  );
};

export default AeroTable4;

